import type { Emitter } from "@core/events/emitter";

export type ToastVariant = "info" | "success" | "warning" | "error";

/** Action button on a confirm-style toast (Unity `ShowAsktoFollowToast`). */
export interface ToastAction {
  readonly id: string;
  readonly label: string;
}

export interface Toast {
  readonly id: string;
  readonly message: string;
  readonly variant: ToastVariant;
  readonly createdAt: number;
  readonly autoDismissMs?: number;
  /**
   * Confirm-style toasts (e.g. the ask-to-follow invitation) render action
   * buttons instead of dismissing on click. The toast dismisses after any
   * action is picked.
   */
  readonly actions?: ReadonlyArray<ToastAction>;
  /** Called with the picked action id before the toast is dismissed. */
  readonly onAction?: (actionId: string) => void;
  /**
   * Work in progress: shows a spinner and cannot be dismissed by clicking.
   *
   * It ends when the work does, so the pusher owns its lifetime — hold the
   * returned toast and `dismiss` it in a `finally`. Never give one an
   * `autoDismissMs`: a busy toast that vanishes on a timer says the work
   * finished when it has not.
   */
  readonly busy?: boolean;
}

/**
 * `IToastsModel` — bottom-bar toast queue surface. Used by the M3 shell
 * (status bar / `ToastsView`) to surface command feedback. Real production
 * notifications (network status, share invites, AI errors) come in M6
 * alongside the cloud transport — this interface stays the seam.
 */
export interface IToastsModel {
  readonly onChanged: Emitter<ReadonlyArray<Toast>>;
  readonly toasts: ReadonlyArray<Toast>;

  push(input: Omit<Toast, "id" | "createdAt">): Toast;
  dismiss(id: string): void;
}

export const TOASTS_MODEL = Symbol.for("Campfire.ToastsModel");
